import React, {
  ComponentProps,
  ComponentType,
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
} from 'react';

import AlloySuspense from './AlloySuspense';

/**
 * A HOC that wraps a given component with an `AlloySuspense` boundary, it is required by `alloy-react-localization`.
 * This allows the wrapped component to handle asynchronous rendering with a fallback UI.
 *
 * @param {ComponentType} Component - The React component to be wrapped.
 * @param [options] - Options for the `AlloySuspense` component.
 * @param [options.fallback=null] - The fallback UI to display while suspense is resolving.
 * @param [options.rest] - Additional props passed to the `AlloySuspense` component.
 *
 * @returns The wrapped component with suspense functionality.
 *
 * @example
 * import WithAlloySuspense from '@adsk/alloy-react-suspense';
 *
 * const MyComponentWithSuspense = WithAlloySuspense(MyComponent, { fallback: <Loading /> });
 *
 * <MyComponentWithSuspense someProp="value" ref={myRef} />;
 *
 * @example
 * import WithAlloySuspense from '@adsk/alloy-react-suspense';
 *
 * const ExampleComponent = React.forwardRef((props, ref) => { ... };
 *
 * export default WithAlloySuspense(ExampleComponent)
 *
 */

export const withAlloySuspense = <P, T>(
  Component: ComponentType<PropsWithoutRef<P> & RefAttributes<T>>,
  {
    fallback = null,
    ...rest
  }: Partial<Omit<ComponentProps<typeof AlloySuspense>, 'children'>> = {},
): ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>> => {
  const ForwardedComponent = forwardRef<T, P>((props, ref) => (
    <AlloySuspense fallback={fallback} {...rest}>
      <Component {...props} ref={ref} />
    </AlloySuspense>
  ));

  ForwardedComponent.displayName = `WithAlloySuspense(${Component.displayName ?? 'Component'})`;

  return ForwardedComponent;
};

export default withAlloySuspense;
